import { createAuthService } from '../auth/client.js';
import { registerPwa } from '../shared/registerPwa.js';
import { initI18n, t } from '../game/utils/i18n.js';

const authService = createAuthService();
registerPwa();
await Promise.all([authService.initialize(), initI18n()]);

const app = document.getElementById('app');
const portalMarkup = `
  <main class="portal-shell">
    <section class="portal-hero">
      <div class="portal-kicker">${t('portal.kicker')}</div>
      <h1>${t('portal.heading')}</h1>
      <p id="portal-status">${t('portal.checkingSession')}</p>
      <div class="portal-actions">
        <button id="portal-logout" class="portal-link" type="button" hidden>${t('portal.logoutBtn')}</button>
      </div>
    </section>
    <section class="portal-grid">
      <article class="portal-tile">
        <div class="portal-tile-eyebrow">${t('portal.tiles.game.eyebrow')}</div>
        <h2>${t('portal.tiles.game.title')}</h2>
        <p>${t('portal.tiles.game.desc')}</p>
        <button id="enter-game" class="portal-button" type="button">${t('portal.tiles.game.btn')}</button>
      </article>
      <article class="portal-tile">
        <div class="portal-tile-eyebrow">${t('portal.tiles.admin.eyebrow')}</div>
        <h2>${t('portal.tiles.admin.title')}</h2>
        <p>${t('portal.tiles.admin.desc')}</p>
        <button id="enter-admin" class="portal-button portal-button-secondary" type="button">${t('portal.tiles.admin.btn')}</button>
      </article>
    </section>
  </main>
`;

app.innerHTML = portalMarkup;

const status = document.getElementById('portal-status');
const logoutButton = document.getElementById('portal-logout');
const gameButton = document.getElementById('enter-game');
const adminButton = document.getElementById('enter-admin');
const actions = document.querySelector('.portal-actions');
const shouldShowPortalChooser = new URLSearchParams(window.location.search).get('portal') === '1';

if (!shouldShowPortalChooser) {
  goToProtectedRoute('game');
}

logoutButton?.addEventListener('click', async () => {
  await authService.logout();
  render(authService.snapshot());
});

gameButton?.addEventListener('click', () => {
  goToProtectedRoute('game');
});

adminButton?.addEventListener('click', () => {
  goToProtectedRoute('admin');
});

render(authService.snapshot());

function render(snapshot) {
  if (!status || !logoutButton || !adminButton) {
    return;
  }

  if (snapshot.loading) {
    status.textContent = t('portal.checkingSession');
    logoutButton.hidden = true;
    adminButton.disabled = true;
    return;
  }

  if (!snapshot.user) {
    const authReason = new URLSearchParams(window.location.search).get('reason');
    if (authReason === 'not_allowed') {
      status.textContent = t('portal.notApproved');
      ensureSwitchAccountButton();
    } else {
      status.textContent = t('portal.noSession');
      removeSwitchAccountButton();
    }
    logoutButton.hidden = true;
    adminButton.disabled = false;
    return;
  }

  const adminStatus = snapshot.user.isAdmin ? t('portal.adminApproved') : t('portal.adminRestricted');
  status.textContent = t('portal.signedInAs', { username: snapshot.user.discordUsername, adminStatus });
  logoutButton.hidden = false;
  removeSwitchAccountButton();
  adminButton.disabled = false;
}

function goToProtectedRoute(target) {
  const destination = resolvePortalTarget(target);
  const snapshot = authService.snapshot();

  if (!snapshot.user) {
    window.location.assign(authService.getDiscordLoginUrl({ returnTo: destination }));
    return;
  }

  window.location.assign(destination);
}

function resolvePortalTarget(target) {
  const { hostname, protocol } = window.location;
  const isLocalHost = ['127.0.0.1', 'localhost'].includes(hostname);

  if (isLocalHost) {
    const currentPort = Number(window.location.port || '3000');
    const port = target === 'game' ? currentPort + 1 : currentPort + 2;
    return `${protocol}//${hostname}:${port}/`;
  }

  const pathname = target === 'game' ? '/game/' : '/admin/';
  return new URL(pathname, window.location.origin).toString();
}

function ensureSwitchAccountButton() {
  if (!actions || document.getElementById('portal-switch-account')) {
    return;
  }

  const button = document.createElement('button');
  button.id = 'portal-switch-account';
  button.className = 'portal-link';
  button.type = 'button';
  button.textContent = t('portal.switchDiscord');
  button.addEventListener('click', async () => {
    await authService.logout();
    window.location.assign(window.location.pathname);
  });
  actions.append(button);
}

function removeSwitchAccountButton() {
  document.getElementById('portal-switch-account')?.remove();
}